import { useState } from "react";
import { Link2 } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Input, Label } from "@/components/ui/Input";

export function SalesOrderLinkApprovalModal({
  orderLabel,
  isLinking,
  isCreating,
  linkError,
  onClose,
  onLink,
  onCreateNew,
}: {
  orderLabel: string;
  isLinking: boolean;
  isCreating: boolean;
  linkError: string | null;
  onClose: () => void;
  onLink: (reference: string) => void;
  onCreateNew: () => void;
}): JSX.Element {
  const [reference, setReference] = useState("");
  const busy = isLinking || isCreating;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4"
      onClick={() => {
        if (!busy) {
          onClose();
        }
      }}
    >
      <div
        className="w-full max-w-lg rounded-lg bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Link approval"
      >
        <div className="border-b border-slate-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-slate-950">Link approval</h2>
          <p className="mt-1 text-sm text-slate-500">
            Paste a hub approval link, an Asana task link, or create a new hub approval for {orderLabel}.
          </p>
        </div>
        <div className="space-y-4 px-5 py-4">
          <div>
            <Label htmlFor="sales-order-approval-reference">Approval link</Label>
            <Input
              id="sales-order-approval-reference"
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              placeholder="https://hub.smartgridsolutions.com/inside-sales/approvals/123"
              className="mt-1"
              disabled={busy}
            />
          </div>
          {linkError ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{linkError}</div>
          ) : null}
        </div>
        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 px-5 py-4">
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="secondary"
            isLoading={isCreating}
            disabled={busy}
            onClick={onCreateNew}
          >
            Create new hub approval
          </Button>
          <Button
            type="button"
            isLoading={isLinking}
            disabled={busy || reference.trim() === ""}
            onClick={() => onLink(reference.trim())}
          >
            <Link2 className="mr-2 h-4 w-4" aria-hidden="true" />
            Link approval
          </Button>
        </div>
      </div>
    </div>
  );
}
